/**
 * <ImportCostMeter /> — small "$X.XX of $Y budget" + thin progress
 * bar at the bottom of ImportDashboard.
 *
 * Per BUILD_READINESS_M29.md acceptance criterion 15: subtle —
 * text-tertiary tone by default. Goes amber > 80% used; rose
 * when fully exhausted.
 *
 * Hidden when budget = 0 (a sentinel for "AI not configured /
 * cost capping disabled").
 */

import { useTranslation } from "react-i18next";

export interface ImportCostMeterProps {
  spentUsd: number;
  budgetUsd: number;
}

export function ImportCostMeter({
  spentUsd,
  budgetUsd,
}: ImportCostMeterProps) {
  const { t } = useTranslation();
  if (!budgetUsd || budgetUsd <= 0) return null;

  const fraction = Math.max(0, Math.min(1, spentUsd / budgetUsd));
  const pct = fraction * 100;
  const alarming = fraction > 0.8;
  const exhausted = fraction >= 1;

  const textCls = exhausted
    ? "text-rose-700"
    : alarming
    ? "text-amber-700"
    : "text-text-tertiary";
  const barCls = exhausted
    ? "bg-rose-500"
    : alarming
    ? "bg-amber-500"
    : "bg-brand-blue/60";

  return (
    <div className="flex items-center gap-2">
      <span
        className={`text-[10px] tabular-nums ${textCls}`}
        aria-label={t("app.icm_cost_used_aria")}
      >
        {t("app.icm_budget_text", {
          spent: spentUsd.toFixed(2),
          budget: budgetUsd.toFixed(2),
        })}
      </span>
      <div
        className="h-0.5 w-16 overflow-hidden rounded-full bg-surface-sunken"
        role="progressbar"
        aria-label={t("app.icm_budget_aria")}
        aria-valuenow={Math.round(pct)}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className={`h-full ${barCls} transition-[width] duration-medium ease-out`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}
